import React, { useState, useRef, useEffect, useCallback } from 'react';
import { AdminLayout } from '@/components/layout/AdminLayout';
import { PlacesAutocompleteV1 } from '@/components/ui/places-autocomplete-v1';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Slider } from '@/components/ui/slider';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { getGoogleMapsApiKey } from '@/lib/api-keys';
import { useToast } from '@/hooks/use-toast';
import { loadGoogleMapsApi, isGoogleMapsLoaded } from '@/lib/google-maps-loader';
import { Search, MapPin, Star, Copy, Filter, Users, Clock, Trash2, ArrowUpDown } from 'lucide-react';

// Tipos para os estabelecimentos encontrados
interface PlaceSearchResult {
  id: string;
  name: string;
  address: string;
  coordinates: { lat: number; lng: number };
  rating: number;
  userRatingCount: number;
  types: string[];
  priceLevel?: number;
  openingHours?: {
    isOpen: boolean;
    weekdayText: string[];
  };
  photos?: string[];
  websiteUrl?: string;
  phoneNumber?: string;
  googleMapsUrl: string;
}

// Filtros disponíveis
interface SearchFilters {
  establishmentTypes: string[];
  minRating: number;
  maxRating: number;
  minReviews: number;
  priceLevel?: number;
  openNow?: boolean;
}

// Tipos de estabelecimento para filtro
const establishmentTypes = [
  { value: 'restaurant', label: 'Restaurantes' },
  { value: 'cafe', label: 'Cafés' },
  { value: 'bar', label: 'Bares' },
  { value: 'lodging', label: 'Hospedagem' },
  { value: 'tourist_attraction', label: 'Atrações Turísticas' },
  { value: 'museum', label: 'Museus' },
  { value: 'park', label: 'Parques' },
  { value: 'amusement_park', label: 'Parques de Diversão' },
  { value: 'shopping_mall', label: 'Shopping Centers' },
  { value: 'spa', label: 'Spas' },
  { value: 'gym', label: 'Academias' },
  { value: 'beauty_salon', label: 'Salões de Beleza' },
  { value: 'hospital', label: 'Hospitais' },
  { value: 'pharmacy', label: 'Farmácias' },
  { value: 'bank', label: 'Bancos' },
  { value: 'gas_station', label: 'Postos de Combustível' }
];

export default function PlacesSearch() {
  const { toast } = useToast();
  const mapRef = useRef<HTMLDivElement>(null);
  
  // Estados principais
  const [searchLocation, setSearchLocation] = useState('');
  const [searchResults, setSearchResults] = useState<PlaceSearchResult[]>([]);
  const [selectedPlaces, setSelectedPlaces] = useState<PlaceSearchResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedLocationData, setSelectedLocationData] = useState<{
    placeId: string;
    coordinates: { lat: number; lng: number };
    name: string;
    address: string;
  } | null>(null);

  // Estados dos filtros
  const [filters, setFilters] = useState<SearchFilters>({
    establishmentTypes: ['restaurant'],
    minRating: 4.5,
    maxRating: 5,
    minReviews: 200,
    priceLevel: undefined,
    openNow: false
  });

  // Estado de ordenação
  const [sortBy, setSortBy] = useState<'rating' | 'reviews'>('rating');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  
  // Estado de ordenação da tabela de selecionados
  const [tableSortBy, setTableSortBy] = useState<'rating' | 'reviews'>('rating');
  const [tableSortOrder, setTableSortOrder] = useState<'asc' | 'desc'>('desc');
  
  // Estado para limite de resultados
  const [resultLimit, setResultLimit] = useState(50);
  
  // Estado para hover no mapa
  const [hoveredPlaceId, setHoveredPlaceId] = useState<string | null>(null);

  // Função para ordenar resultados
  const sortedResults = React.useMemo(() => {
    return [...searchResults].sort((a, b) => {
      let comparison = 0;
      
      if (sortBy === 'rating') {
        comparison = a.rating - b.rating;
      } else if (sortBy === 'reviews') {
        comparison = a.userRatingCount - b.userRatingCount;
      }
      
      return sortOrder === 'asc' ? comparison : -comparison;
    });
  }, [searchResults, sortBy, sortOrder]);

  // Função para ordenar resultados selecionados
  const sortedSelectedPlaces = React.useMemo(() => {
    return [...selectedPlaces].sort((a, b) => {
      let comparison = 0;
      
      if (tableSortBy === 'rating') {
        comparison = a.rating - b.rating;
      } else if (tableSortBy === 'reviews') {
        comparison = a.userRatingCount - b.userRatingCount;
      }
      
      return tableSortOrder === 'asc' ? comparison : -comparison;
    });
  }, [selectedPlaces, tableSortBy, tableSortOrder]);

  // Estados para o mapa
  const [mapsLoaded, setMapsLoaded] = useState(false);
  const [mapInitialized, setMapInitialized] = useState(false);
  const mapInstanceRef = useRef<google.maps.Map | null>(null);
  const markersRef = useRef<google.maps.Marker[]>([]);

  // Carregar a API do Google Maps
  useEffect(() => {
    if (!mapsLoaded) {
      loadGoogleMapsApi()
        .then(() => {
          setMapsLoaded(true);
        })
        .catch(error => {
          console.error('Erro ao carregar a API do Google Maps:', error);
        });
    }
  }, [mapsLoaded]);

  // Inicializar o mapa
  useEffect(() => {
    if (!mapsLoaded || !mapRef.current || sortedResults.length === 0) return;

    try {
      // Limpar marcadores anteriores
      markersRef.current.forEach(marker => marker.setMap(null));
      markersRef.current = [];

      // Calcular o centro do mapa
      const center = sortedResults.reduce(
        (acc, place) => {
          acc.lat += place.coordinates.lat / sortedResults.length;
          acc.lng += place.coordinates.lng / sortedResults.length;
          return acc;
        },
        { lat: 0, lng: 0 }
      );

      // Criar o mapa
      const map = new window.google.maps.Map(mapRef.current, {
        center,
        zoom: sortedResults.length === 1 ? 15 : 12,
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: false,
        styles: [
          {
            featureType: "poi",
            elementType: "labels",
            stylers: [{ visibility: "off" }]
          }
        ]
      });

      mapInstanceRef.current = map;

      // Adicionar marcadores para cada resultado
      sortedResults.forEach((place, index) => {
        const marker = new window.google.maps.Marker({
          position: { lat: place.coordinates.lat, lng: place.coordinates.lng },
          map,
          title: `${index + 1}. ${place.name}`,
          label: {
            text: `${index + 1}`,
            color: "#FFFFFF",
            fontWeight: "bold"
          },
          icon: {
            path: window.google.maps.SymbolPath.CIRCLE,
            fillColor: "#0D9488", // Cor padrão
            fillOpacity: 1,
            strokeWeight: 0,
            scale: 12
          },
          zIndex: index
        });

        markersRef.current.push(marker);

        // Adicionar evento de clique
        marker.addListener("click", () => {
          // Verificar se o item já está selecionado
          const isSelected = selectedPlaces.some(p => p.id === place.id);
          
          // Alternar seleção
          if (isSelected) {
            // Remover da seleção
            setSelectedPlaces(prev => prev.filter(p => p.id !== place.id));
          } else {
            // Adicionar à seleção
            setSelectedPlaces(prev => [...prev, place]);
          }
        });
      });

      // Ajustar o zoom para mostrar todos os marcadores
      if (sortedResults.length > 1) {
        const bounds = new window.google.maps.LatLngBounds();
        sortedResults.forEach(place => {
          bounds.extend({ lat: place.coordinates.lat, lng: place.coordinates.lng });
        });
        map.fitBounds(bounds);
      }

      setMapInitialized(true);
    } catch (error) {
      console.error("Erro ao inicializar o mapa:", error);
    }
  }, [mapsLoaded, sortedResults]); // Removido hoveredPlaceId das dependências

  // Função para destacar marcador no hover e mostrar seleção
  const highlightMarker = useCallback((placeId: string | null) => {
    if (!mapInstanceRef.current) return;

    markersRef.current.forEach((marker, index) => {
      const place = sortedResults[index];
      if (place) {
        const isHighlighted = place.id === placeId;
        const isSelected = selectedPlaces.some(p => p.id === place.id);
        
        // Determinar cor baseada no estado
        let fillColor = "#0D9488"; // Cor padrão (azul)
        let scale = 12; // Tamanho padrão
        
        if (isSelected) {
          fillColor = "#EF4444"; // Vermelho para selecionados
          scale = 14; // Um pouco maior
        }
        
        if (isHighlighted) {
          fillColor = "#F97316"; // Laranja para hover
          scale = 16; // Maior quando hover
        }
        
        marker.setIcon({
          path: window.google.maps.SymbolPath.CIRCLE,
          fillColor: fillColor,
          fillOpacity: 1,
          strokeWeight: isSelected ? 2 : 0, // Borda para selecionados
          strokeColor: isSelected ? "#FFFFFF" : "", // Borda branca para selecionados
          scale: scale
        });
        
        marker.setZIndex(isHighlighted ? 100 : (isSelected ? 50 : index));
      }
    });
  }, [sortedResults, selectedPlaces]);

  // Efeito para destacar marcador no hover e atualizar seleção
  React.useEffect(() => {
    highlightMarker(hoveredPlaceId);
  }, [hoveredPlaceId, selectedPlaces, highlightMarker]);

  // Função para fazer uma única chamada da API
  const makeApiCall = async (pageToken?: string) => {
    const apiKey = getGoogleMapsApiKey();
    if (!apiKey) {
      throw new Error("Chave da API do Google Maps não configurada.");
    }

    // Construir parâmetros da busca
    const searchParams: any = {
      languageCode: "pt-BR",
      maxResultCount: 20, // Máximo por chamada
      locationBias: {
        circle: {
          center: {
            latitude: selectedLocationData!.coordinates.lat,
            longitude: selectedLocationData!.coordinates.lng
          },
          radius: 5000 // 5km de raio
        }
      }
    };

    // Adicionar pageToken se fornecido
    if (pageToken) {
      searchParams.pageToken = pageToken;
    }

    // Adicionar filtros de tipo se selecionados
    if (filters.establishmentTypes.length > 0) {
      // Usar textQuery com tipos específicos
      const typeQueries = filters.establishmentTypes.map(type => {
        const typeLabels: { [key: string]: string } = {
          'restaurant': 'restaurante',
          'cafe': 'café',
          'bar': 'bar',
          'lodging': 'hotel hospedagem',
          'tourist_attraction': 'atração turística',
          'museum': 'museu',
          'park': 'parque',
          'amusement_park': 'parque de diversão',
          'shopping_mall': 'shopping centro comercial',
          'spa': 'spa',
          'gym': 'academia fitness',
          'beauty_salon': 'salão de beleza',
          'hospital': 'hospital',
          'pharmacy': 'farmácia',
          'bank': 'banco',
          'gas_station': 'posto de combustível'
        };
        return typeLabels[type] || type;
      });
      searchParams.textQuery = `${typeQueries.join(' OR ')} em ${selectedLocationData!.name}`;
    } else {
      searchParams.textQuery = `estabelecimentos em ${selectedLocationData!.name}`;
    }

    const response = await fetch('https://places.googleapis.com/v1/places:searchText', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Goog-Api-Key': apiKey,
        'X-Goog-FieldMask': 'places.displayName,places.formattedAddress,places.location,places.id,places.rating,places.userRatingCount,places.types,places.priceLevel,places.currentOpeningHours,places.photos,places.websiteUri,places.nationalPhoneNumber,places.googleMapsUri,nextPageToken'
      },
      body: JSON.stringify(searchParams)
    });

    if (!response.ok) {
      throw new Error(`Erro na busca: ${response.status}`);
    }

    return await response.json();
  };

  // Função para buscar lugares próximos usando múltiplas chamadas da API
  const searchNearbyPlaces = async () => {
    if (!selectedLocationData) {
      toast({
        title: "Local necessário",
        description: "Selecione um local primeiro para buscar estabelecimentos próximos.",
        variant: "destructive"
      });
      return;
    }

    setIsLoading(true);
    setSearchResults([]);

    try {
      let allResults: PlaceSearchResult[] = [];
      let pageToken: string | undefined;
      let callCount = 0;
      const maxCalls = Math.ceil(resultLimit / 20); // Número máximo de chamadas necessárias

      do {
        callCount++;
        console.log(`Fazendo chamada ${callCount} da API...`);
        
        const data = await makeApiCall(pageToken);
        
        if (data.places && data.places.length > 0) {
          const newResults: PlaceSearchResult[] = data.places
            .map((place: any) => ({
              id: place.id,
              name: place.displayName?.text || 'Nome não disponível',
              address: place.formattedAddress || '',
              coordinates: {
                lat: place.location?.latitude || 0,
                lng: place.location?.longitude || 0
              },
              rating: place.rating || 0,
              userRatingCount: place.userRatingCount || 0,
              types: place.types || [],
              priceLevel: place.priceLevel,
              openingHours: place.currentOpeningHours ? {
                isOpen: place.currentOpeningHours.openNow || false,
                weekdayText: place.currentOpeningHours.weekdayDescriptions || []
              } : undefined,
              photos: place.photos?.map((photo: any) => photo.name) || [],
              websiteUrl: place.websiteUri,
              phoneNumber: place.nationalPhoneNumber,
              googleMapsUrl: place.googleMapsUri || `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(place.displayName?.text || '')}&query_place_id=${place.id}`
            }))
            .filter((place: PlaceSearchResult) => {
              // Aplicar filtros
              if (place.rating < filters.minRating || place.rating > filters.maxRating) {
                return false;
              }
              if (place.userRatingCount < filters.minReviews) {
                return false;
              }
              if (filters.priceLevel !== undefined && place.priceLevel !== filters.priceLevel) {
                return false;
              }
              if (filters.openNow && place.openingHours && !place.openingHours.isOpen) {
                return false;
              }
              return true;
            });

          allResults = [...allResults, ...newResults];
          
          // Atualizar resultados a cada chamada para mostrar progresso
          setSearchResults([...allResults]);
          
          // Aguardar um pouco entre as chamadas para evitar rate limiting
          if (data.nextPageToken && callCount < maxCalls) {
            await new Promise(resolve => setTimeout(resolve, 200));
          }
          
          pageToken = data.nextPageToken;
        } else {
          break; // Não há mais resultados
        }
      } while (pageToken && callCount < maxCalls && allResults.length < resultLimit);

      // Limitar aos resultados solicitados
      const finalResults = allResults.slice(0, resultLimit);
      setSearchResults(finalResults);
    } catch (error) {
      console.error('Erro na busca:', error);
      toast({
        title: "Erro na busca",
        description: "Ocorreu um erro ao buscar estabelecimentos. Tente novamente.",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Handler para seleção de local
  const handleLocationSelect = (locationData: any) => {
    setSelectedLocationData({
      placeId: locationData.placeId,
      coordinates: locationData.coordinates,
      name: locationData.name,
      address: locationData.address
    });
  };

  // Handlers para seleção de estabelecimentos
  const handlePlaceSelection = (place: PlaceSearchResult, selected: boolean) => {
    if (selected) {
      setSelectedPlaces(prev => [...prev, place]);
    } else {
      setSelectedPlaces(prev => prev.filter(p => p.id !== place.id));
    }
  };

  // Handler para ordenação da tabela
  const handleTableSort = (column: 'rating' | 'reviews') => {
    if (tableSortBy === column) {
      // Se clicar na mesma coluna, inverte a ordem
      setTableSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      // Se clicar em coluna diferente, define a coluna e ordem padrão desc
      setTableSortBy(column);
      setTableSortOrder('desc');
    }
  };

  // Função para copiar dados selecionados para clipboard
  const copySelectedToClipboard = () => {
    if (selectedPlaces.length === 0) {
      toast({
        title: "Nenhum item selecionado",
        description: "Selecione pelo menos um estabelecimento para copiar.",
        variant: "destructive"
      });
      return;
    }

    const markdownData = selectedPlaces.map((place, index) => {
      return `## ${index + 1}. ${place.name}

**Endereço:** ${place.address || 'Endereço não disponível'}
**Rating:** ${place.rating || 'N/A'}⭐ (${place.userRatingCount || 0} avaliações)

---
`;
    }).join('\n');

    navigator.clipboard.writeText(markdownData).then(() => {
      toast({
        title: "Copiado!",
        description: `${selectedPlaces.length} item(s) copiado(s) para o clipboard.`,
      });
    }).catch(() => {
      toast({
        title: "Erro ao copiar",
        description: "Não foi possível copiar os dados.",
        variant: "destructive"
      });
    });
  };

  return (
    <AdminLayout title="Busca de Lugares">
      <div className="space-y-6">
        {/* Seção de busca e filtros */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Search className="h-5 w-5" />
              Buscar Estabelecimentos
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Campo de local */}
            <div className="space-y-2">
              <Label htmlFor="location">Local de Busca</Label>
              <PlacesAutocompleteV1
                value={searchLocation}
                onChange={setSearchLocation}
                onPlaceSelect={handleLocationSelect}
                placeholder="Digite o local onde deseja buscar estabelecimentos"
                className="w-full"
              />
              {selectedLocationData && (
                <div className="text-sm text-green-600 flex items-center gap-1">
                  <MapPin className="h-4 w-4" />
                  Local selecionado: {selectedLocationData.name}
                </div>
              )}
            </div>

            {/* Filtros */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* Tipo de estabelecimento */}
              <div className="space-y-2">
                <Label>Tipo de Estabelecimento</Label>
                <Select
                  value={filters.establishmentTypes[0] || 'all'}
                  onValueChange={(value) => {
                    if (value && value !== 'all') {
                      setFilters(prev => ({
                        ...prev,
                        establishmentTypes: [value]
                      }));
                    } else {
                      setFilters(prev => ({
                        ...prev,
                        establishmentTypes: []
                      }));
                    }
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Todos os tipos" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos os tipos</SelectItem>
                    {establishmentTypes.map(type => (
                      <SelectItem key={type.value} value={type.value}>
                        {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Rating mínimo */}
              <div className="space-y-2">
                <Label>Rating Mínimo: {filters.minRating.toFixed(1)}</Label>
                <Slider
                  value={[filters.minRating]}
                  onValueChange={([value]) => setFilters(prev => ({ ...prev, minRating: value }))}
                  max={5}
                  min={0}
                  step={0.1}
                  className="w-full"
                />
              </div>

              {/* Número mínimo de avaliações */}
              <div className="space-y-2">
                <Label htmlFor="minReviews">Mín. Avaliações</Label>
                <Input
                  id="minReviews"
                  type="number"
                  value={filters.minReviews || ''}
                  onChange={(e) => {
                    const value = e.target.value;
                    setFilters(prev => ({ 
                      ...prev, 
                      minReviews: value === '' ? 0 : parseInt(value) || 0 
                    }));
                  }}
                  min="0"
                  placeholder="0"
                />
              </div>

              {/* Limite de resultados */}
              <div className="space-y-2">
                <Label>Limite de Resultados</Label>
                <Input
                  type="number"
                  value={resultLimit || ''}
                  onChange={(e) => {
                    const value = e.target.value;
                    setResultLimit(value === '' ? 50 : parseInt(value) || 50);
                  }}
                  min="5"
                  max="100"
                  className="w-full"
                  placeholder="50"
                />
              </div>

              {/* Botão de busca */}
              <div className="flex items-end">
                <Button 
                  onClick={searchNearbyPlaces}
                  disabled={!selectedLocationData || isLoading}
                  className="w-full"
                >
                  {isLoading ? 'Buscando...' : 'Buscar'}
                </Button>
              </div>
            </div>

            {/* Ordenação */}
            {searchResults.length > 0 && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Ordenar por</Label>
                  <Select
                    value={sortBy}
                    onValueChange={(value: 'rating' | 'reviews') => setSortBy(value)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="rating">Rating</SelectItem>
                      <SelectItem value="reviews">Número de Avaliações</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Ordem</Label>
                  <Select
                    value={sortOrder}
                    onValueChange={(value: 'asc' | 'desc') => setSortOrder(value)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="desc">Maior para Menor</SelectItem>
                      <SelectItem value="asc">Menor para Maior</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Resultados */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Lista de resultados */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span>Resultados ({sortedResults.length})</span>
                {selectedPlaces.length > 0 && (
                  <Badge variant="secondary">{selectedPlaces.length} selecionados</Badge>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {sortedResults.map((place, index) => (
                  <div
                    key={place.id}
                    className="flex items-start gap-3 p-3 border rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
                    onMouseEnter={() => setHoveredPlaceId(place.id)}
                    onMouseLeave={() => setHoveredPlaceId(null)}
                  >
                    <Checkbox
                      checked={selectedPlaces.some(p => p.id === place.id)}
                      onCheckedChange={(checked) => handlePlaceSelection(place, checked as boolean)}
                    />
                    <div className="flex-1 min-w-0 text-left">
                      <h4 className="font-medium text-sm">
                        <span className="inline-flex items-center justify-center w-6 h-6 bg-voah-teal text-white text-xs font-bold rounded-full mr-2">
                          {index + 1}
                        </span>
                        {place.name}
                      </h4>
                      <p className="text-xs text-gray-600 truncate text-left">{place.address}</p>
                      <div className="flex items-center gap-2 mt-1 justify-start">
                        <div className="flex items-center gap-1">
                          <Star className="h-3 w-3 text-yellow-500 fill-current" />
                          <span className="text-xs">{place.rating.toFixed(1)}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <Users className="h-3 w-3 text-gray-400" />
                          <span className="text-xs">{place.userRatingCount}</span>
                        </div>
                        {place.openingHours?.isOpen !== undefined && (
                          <div className="flex items-center gap-1">
                            <Clock className="h-3 w-3 text-gray-400" />
                            <span className={`text-xs ${place.openingHours.isOpen ? 'text-green-600' : 'text-red-600'}`}>
                              {place.openingHours.isOpen ? 'Aberto' : 'Fechado'}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
                {sortedResults.length === 0 && !isLoading && (
                  <div className="text-center text-gray-500 py-8">
                    Selecione um local e clique em "Buscar" para ver estabelecimentos próximos
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Mapa */}
          <Card>
            <CardHeader>
              <CardTitle>Mapa</CardTitle>
            </CardHeader>
            <CardContent>
              <div ref={mapRef} className="w-full h-96 bg-gray-100 rounded-lg" />
              {!mapsLoaded && (
                <div className="w-full h-96 bg-gray-100 rounded-lg flex items-center justify-center">
                  <span className="text-gray-500">Carregando mapa...</span>
                </div>
              )}
              

            </CardContent>
          </Card>
        </div>

        {/* Tabela de selecionados */}
        {selectedPlaces.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span>Estabelecimentos Selecionados</span>
                <div className="flex items-center gap-2">
                  <Button 
                    onClick={() => setSelectedPlaces([])} 
                    variant="outline" 
                    size="sm" 
                    className="flex items-center gap-2"
                  >
                    <Trash2 className="h-4 w-4" />
                    Limpar Seleção
                  </Button>
                  <Button onClick={copySelectedToClipboard} size="sm" className="flex items-center gap-2">
                    <Copy className="h-4 w-4" />
                    Copy to Clipboard
                  </Button>
                </div>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Nome</TableHead>
                      <TableHead>Endereço</TableHead>
                      <TableHead>
                        <button
                          onClick={() => handleTableSort('rating')}
                          className="flex items-center gap-1 hover:text-blue-600 transition-colors"
                        >
                          Rating
                          <ArrowUpDown className="h-4 w-4" />
                        </button>
                      </TableHead>
                      <TableHead>
                        <button
                          onClick={() => handleTableSort('reviews')}
                          className="flex items-center gap-1 hover:text-blue-600 transition-colors"
                        >
                          Avaliações
                          <ArrowUpDown className="h-4 w-4" />
                        </button>
                      </TableHead>
                      <TableHead>Ações</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {sortedSelectedPlaces.map((place) => (
                      <TableRow key={place.id}>
                        <TableCell className="font-medium">
                          <button
                            onClick={() => {
                              if (place.googleMapsUrl) {
                                window.open(place.googleMapsUrl, '_blank');
                              }
                            }}
                            className="hover:text-blue-600 hover:underline transition-colors"
                            title="Abrir no Google Maps"
                          >
                            {place.name}
                          </button>
                        </TableCell>
                        <TableCell className="max-w-xs truncate">{place.address}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <Star className="h-4 w-4 text-yellow-500 fill-current" />
                            {place.rating.toFixed(1)}
                          </div>
                        </TableCell>
                        <TableCell>{place.userRatingCount}</TableCell>
                        <TableCell>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handlePlaceSelection(place, false)}
                          >
                            Remover
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </AdminLayout>
  );
} 